import React from 'react';
import Image from 'next/image';
import Danger from '@/assets/danger.svg';
import CheckIcon from '@/assets/dark-check-outline.svg';
import UncheckIcon from '@/assets/uncheck-outline.svg';
import { SANITIZE_OPTIONS } from '@/utils/utils';
import sanitizeHtml from 'sanitize-html';

// Base form field
interface FormFieldProps {
	label: string;
	id: string;
	name: string;
	type: string;
	error?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	testId?: string;
	disabled?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({ label, id, name, type, error, onChange, testId, disabled }) => (
	<div>
		<div className="text-lg font-semibold text-grey-800">{label}</div>
		<div className="relative">
			<input
				className={`mt-1.5 ${error ? 'input-error' : ''}`}
				data-testid={testId}
				id={id}
				name={name}
				type={type}
				onChange={onChange}
				disabled={disabled}
			/>
			{error && (
				<Image
					src={Danger.src}
					alt="Error"
					width={24}
					height={24}
					className="absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2"
				/>
			)}
		</div>
		{error && (
			<div
				className="mt-1 text-body-medium text-red-800"
				dangerouslySetInnerHTML={{
					__html: sanitizeHtml(error, SANITIZE_OPTIONS),
				}}
			/>
		)}
	</div>
);

// Name fields
interface NameFieldsProps {
	testIdPrefix?: string;
}

export const NameFields: React.FC<NameFieldsProps> = ({ testIdPrefix = '' }) => (
	<div className="flex gap-4">
		<div className="w-1/2">
			<FormField
				label="First Name"
				id="first-name"
				name="first-name"
				type="text"
				testId={`${testIdPrefix}first-name-input`}
			/>
		</div>
		<div className="w-1/2">
			<FormField
				label="Last Name"
				id="last-name"
				name="last-name"
				type="text"
				testId={`${testIdPrefix}last-name-input`}
			/>
		</div>
	</div>
);

// Password field
interface PasswordRequirement {
	text: string;
	isMet: boolean;
}

interface PasswordFieldProps {
	error?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	showRequirements?: boolean;
	passwordRequirements?: {
		minLength: boolean;
		uppercase: boolean;
		number: boolean;
		specialChar: boolean;
	};
	testId?: string;
	showPassword?: boolean;
	disabled?: boolean;
}

const PasswordRequirementItem = ({ text, isMet }: PasswordRequirement) => (
	<div className="flex items-center space-x-2">
		<Image
			src={isMet ? CheckIcon.src : UncheckIcon.src}
			alt={isMet ? 'requirement met' : 'requirement not met'}
			width={24}
			height={24}
		/>
		<span className="text-body-medium text-grey-400">{text}</span>
	</div>
);

export const PasswordRequirements = ({ passwordRequirements, showRequirements }: PasswordFieldProps) => {
	if (!showRequirements || !passwordRequirements) return null;

	const requirementsList: PasswordRequirement[] = [
		{ text: 'Minimum 8 characters', isMet: passwordRequirements.minLength },
		{ text: 'At least 1 uppercase letter', isMet: passwordRequirements.uppercase },
		{ text: 'At least 1 number', isMet: passwordRequirements.number },
		{ text: 'At least 1 special character (@$!%*#?&)', isMet: passwordRequirements.specialChar },
	];

	return (
		<div className="mt-2 space-y-0.5">
			{requirementsList.map((requirement) => (
				<PasswordRequirementItem key={requirement.text} text={requirement.text} isMet={requirement.isMet} />
			))}
		</div>
	);
};

export const PasswordField: React.FC<PasswordFieldProps> = ({
	error,
	onChange,
	showRequirements = false,
	passwordRequirements,
	testId,
	disabled,
	showPassword = false,
}) => (
	<>
		<FormField
			label="Password"
			id="password"
			name="password"
			type={showRequirements || showPassword ? 'text' : 'password'}
			error={error}
			onChange={onChange}
			testId={testId}
			disabled={disabled}
		/>

		<PasswordRequirements passwordRequirements={passwordRequirements} showRequirements={showRequirements} />
	</>
);
